import type { AdminRole, AdminUser } from '@/types/admin'

/**
 * Client-side role checks are a UX affordance only — they hide buttons the user cannot use.
 *
 * The actual enforcement is `RolesGuard` in the `store-backend` repository
 * (`libs/auth/src/roles.guard.ts`), which runs on every admin request and is default-deny, so a
 * route with no policy is refused rather than opened. These lists must be kept in step with the
 * policies there; being wrong here shows the wrong buttons, not the wrong data.
 */

export const ROLE_LABELS: Record<AdminRole, string> = {
  SuperAdmin: 'Super admin',
  Admin: 'Admin',
  OrderManager: 'Order manager',
  Warehouse: 'Warehouse',
  CustomerSupport: 'Customer support',
  ReadOnly: 'Read only',
}

/**
 * @deprecated Roles are editable rows now, so a role NAME decides nothing — two installs can have
 * a "Dispatch" role meaning different things, and a staff member may be on a role this list has
 * never heard of. Gate on a permission via {@link import('./permissions').can} instead.
 *
 * Kept only while the last call sites are migrated. Returns false for anyone on a custom role,
 * which errs toward hiding a control rather than showing one that will 403.
 */
export function hasRole(
  user: Pick<AdminUser, 'role'> | null | undefined,
  roles: readonly AdminRole[],
): boolean {
  return user?.role ? roles.includes(user.role) : false
}
